import { consola } from 'consola'
import { ref, computed, nextTick } from 'vue'
import { useRuntimeConfig } from '#imports'

/**
 * Composable для безопасной инициализации institutionID
 * Ожидает готовности переменных окружения перед выполнением запросов
 */
export const useInstitutionIdInitializer = () => {
  const isReady = ref(false)
  const initializationError = ref<string | null>(null)
  const institutionId = ref<number | null>(null)

  /**
   * Инициализация institutionID с ожиданием готовности конфигурации
   */
  const initialize = async (): Promise<number> => {
    if (isReady.value && institutionId.value) {
      return institutionId.value
    }

    try {
      consola.debug('🔄 Initializing InstitutionID...')

      const config = useRuntimeConfig()

      // Проверяем что конфигурация загружена
      if (!config.public || !config.public.institutionID) {
        // Даем время на инициализацию конфигурации
        await new Promise(resolve => setTimeout(resolve, 100))
        await nextTick()
      }

      const configInstitutionId = config.public?.institutionID
      if (!configInstitutionId || configInstitutionId === 'undefined') {
        throw new Error('InstitutionID not found in runtime configuration')
      }

      institutionId.value = Number(configInstitutionId)

      if (Number.isNaN(institutionId.value) || institutionId.value <= 0) {
        throw new Error(`Invalid InstitutionID: ${configInstitutionId}`)
      }

      consola.debug('🌐 InstitutionID from runtime config:', institutionId.value)

      isReady.value = true
      initializationError.value = null
      
      consola.debug('✅ InstitutionID initialized successfully:', institutionId.value)
      return institutionId.value

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to initialize InstitutionID'
      initializationError.value = errorMessage
      consola.error('❌ InstitutionID initialization failed:', error)
      
      // Используем fallback значение
      institutionId.value = 0
      isReady.value = true
      
      consola.warn('⚠️ Using fallback InstitutionID:', institutionId.value)
      return institutionId.value
    }
  }

  /**
   * Получить InstitutionID с автоматической инициализацией если необходимо
   */
  const getInstitutionId = async (): Promise<number> => {
    if (isReady.value && institutionId.value) {
      return institutionId.value
    }
    return await initialize()
  }

  /**
   * Проверить готовность без инициализации
   */
  const checkReadiness = (): boolean => {
    return isReady.value && institutionId.value !== null
  }

  /**
   * Сбросить состояние (для тестирования)
   */
  const reset = (): void => {
    isReady.value = false
    institutionId.value = null
    initializationError.value = null
  }

  return {
    isReady: computed(() => isReady.value),
    institutionId: computed(() => institutionId.value),
    initializationError: computed(() => initializationError.value),
    initialize,
    getInstitutionId,
    checkReadiness,
    reset,
  }
}

// Глобальный экземпляр для использования по всему приложению
const globalInstitutionIdInitializer = useInstitutionIdInitializer()

/**
 * Функция для получения готового к использованию InstitutionID
 * Автоматически инициализирует если необходимо
 */
export const getReadyInstitutionId = async (): Promise<number> => {
  return await globalInstitutionIdInitializer.getInstitutionId()
}

/**
 * Функция для проверки готовности InstitutionID без инициализации
 */
export const isInstitutionIdReady = (): boolean => {
  return globalInstitutionIdInitializer.checkReadiness()
}
